/**
 * The shared definition of "does this person match what was typed" for people
 * pickers — one place to change how searching for a colleague behaves.
 *
 * A person matches on:
 *   - name or email, as a case- and accent-insensitive substring
 *   - initials, as a prefix — both the initials derived from their name
 *     ("Jaan Tallinn" is found by "jt") and any explicit short handle the
 *     caller passes (a round member's "VB").
 *
 * A person is deliberately NOT matched on their user id. Ids are opaque
 * generated strings that no one types on purpose, and including them meant a
 * query could silently match a row whose name and email had nothing to do with
 * it.
 *
 * Substring for names/emails, prefix for initials: initials are a two- or
 * three-letter handle, so "contains" would match almost anything, while a
 * prefix keeps "vb" pointed at VB.
 *
 * The character-level rules — casing, accents, whitespace — are shared with
 * every other picker and live in ./search-text.
 */
import {
  filterBySearch,
  matchesAnyField,
  normalizeSearchText,
} from "./search-text";

/** A person as far as searching is concerned — every field optional but name. */
export type SearchablePerson = {
  name?: string | null;
  email?: string | null;
  /**
   * An explicit short handle for this person in the caller's context, when one
   * exists — e.g. a round member's initials or evaluator shortname. Distinct
   * from the initials derived from `name`; both are searched.
   */
  initials?: string | null;
};

/** Everything except letters and digits, so "j.t." and "VB (F)" compare cleanly. */
function toInitialsKey(value?: string | null): string {
  return normalizeSearchText(value).replace(/[^a-z0-9]/g, "");
}

/**
 * The full run of first letters in a name: "Anna Maria Lopez" -> "aml".
 *
 * Not `getInitials` from ./utils, which exists to fill an avatar circle: that
 * one caps at two characters and pads a single-word name with its second
 * letter, so "aml" would never find Anna Maria Lopez. Search wants the whole
 * sequence. The avatar's two-letter form still works here, because it is a
 * prefix of this one.
 */
export function deriveNameInitials(name?: string | null): string {
  return normalizeSearchText(name)
    .split(" ")
    .map((word) => word.replace(/[^a-z0-9]/g, "").charAt(0))
    .join("");
}

/**
 * Does this person match the query? A blank query matches everyone, so callers
 * can pass raw input straight through without special-casing the empty box.
 */
export function matchesPersonSearch(
  person: SearchablePerson,
  query: string,
): boolean {
  if (!normalizeSearchText(query)) return true;

  if (matchesAnyField(query, [person.name, person.email])) return true;

  const initialsQuery = toInitialsKey(query);
  if (!initialsQuery) return false;

  const explicitInitials = toInitialsKey(person.initials);
  if (explicitInitials && explicitInitials.startsWith(initialsQuery)) {
    return true;
  }

  const derivedInitials = deriveNameInitials(person.name);
  return derivedInitials !== "" && derivedInitials.startsWith(initialsQuery);
}

/**
 * Filter a roster, preserving the caller's ordering — pickers order their list
 * themselves (recently used first, round members next) and searching must not
 * reshuffle it.
 */
export function filterPeopleBySearch<T extends SearchablePerson>(
  people: readonly T[],
  query: string,
): T[] {
  return filterBySearch(people, query, matchesPersonSearch);
}
